import { palette } from "./colors";

const primaryFontFamily = '"BasierCircle", sans-serif';
const monospaceFontFamily =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

export const fontFamily = {
  primary: primaryFontFamily,
  monospace: monospaceFontFamily,
} as const;

export const fontSize = {
  h1: "6rem",
  h2: "3.75rem",
  h3: "3rem",
  h4: "2.125rem",
  h5: "1.5rem",
  h6: "1.25rem",
  subtitle1: "1rem",
  subtitle2: "0.875rem",
  body1: "1rem",
  body2: "0.875rem",
  caption: "0.75rem",
  overline: "0.75rem",
  button: "0.875rem",
} as const;

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

const headingLineHeight = 1.25;
const textLineHeight = 1.5;

const basierStyle = (
  size: string,
  weight: (typeof fontWeight)[keyof typeof fontWeight],
  lineHeight: number,
) =>
  ({
    fontFamily: fontFamily.primary,
    fontSize: size,
    fontWeight: weight,
    lineHeight,
  }) as const;

/**
 * Named Basier Circle type scale. Sizes are rem at a 16px root.
 * Headings use semibold (600) and 125% line height. Body, label, small, and
 * caption use 150% line height. Bold (700) is not part of this scale.
 * MUI variants in `typography` stay unchanged for existing screens.
 */
export const typeScale = {
  heading: {
    xl: basierStyle("1.5rem", fontWeight.semibold, headingLineHeight),
    lg: basierStyle("1.25rem", fontWeight.semibold, headingLineHeight),
    md: basierStyle("1.125rem", fontWeight.semibold, headingLineHeight),
    sm: basierStyle("1rem", fontWeight.semibold, headingLineHeight),
    xs: basierStyle("0.875rem", fontWeight.semibold, headingLineHeight),
  },
  body: {
    md: basierStyle("1rem", fontWeight.regular, textLineHeight),
    sm: basierStyle("0.875rem", fontWeight.regular, textLineHeight),
  },
  label: {
    md: basierStyle("0.875rem", fontWeight.medium, textLineHeight),
  },
  small: {
    strong: basierStyle("0.75rem", fontWeight.semibold, textLineHeight),
  },
  caption: {
    md: basierStyle("0.75rem", fontWeight.regular, textLineHeight),
  },
} as const;

export type TTypeScale = typeof typeScale;

export const paragraphSpacing = {
  sm: "0.75em",
  md: "1em",
  lg: "1.5em",
} as const;

export const textDecoration = {
  none: "none",
  underline: "underline",
  lineThrough: "line-through",
} as const;

export const lineHeight = {
  h1: 1.167,
  h2: 1.2,
  h3: 1.167,
  h4: 1.235,
  h5: 1.334,
  h6: 1.6,
  subtitle1: 1.75,
  subtitle2: 1.57,
  body1: 1.5,
  body2: 1.43,
  caption: 1.66,
  overline: 2.66,
  button: 1.75,
} as const;

export const typography = {
  htmlFontSize: 16,
  fontSize: 14,
  fontFamily: fontFamily.primary,
  h1: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.light,
    fontSize: fontSize.h1,
    lineHeight: lineHeight.h1,
    letterSpacing: "-0.01562em",
  },
  h2: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.light,
    fontSize: fontSize.h2,
    lineHeight: lineHeight.h2,
    letterSpacing: "-0.00833em",
  },
  h3: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.h3,
    lineHeight: lineHeight.h3,
    letterSpacing: "0em",
  },
  h4: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.h4,
    lineHeight: lineHeight.h4,
    letterSpacing: "0.00735em",
  },
  h5: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.h5,
    lineHeight: lineHeight.h5,
    letterSpacing: "0em",
  },
  h6: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.h6,
    lineHeight: lineHeight.h6,
    letterSpacing: "0.0075em",
  },
  subtitle1: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.subtitle1,
    lineHeight: lineHeight.subtitle1,
    letterSpacing: "0.00938em",
  },
  subtitle2: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.subtitle2,
    lineHeight: lineHeight.subtitle2,
    letterSpacing: "0.00714em",
  },
  body1: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.body1,
    lineHeight: lineHeight.body1,
    letterSpacing: "0.00938em",
  },
  body2: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.body2,
    lineHeight: lineHeight.body2,
    letterSpacing: "0.01071em",
  },
  button: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.medium,
    fontSize: fontSize.button,
    lineHeight: lineHeight.button,
    letterSpacing: "0.02857em",
    textTransform: "unset",
  },
  caption: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.caption,
    lineHeight: lineHeight.caption,
    letterSpacing: "0.03333em",
  },
  overline: {
    fontFamily: fontFamily.primary,
    fontWeight: fontWeight.regular,
    fontSize: fontSize.overline,
    lineHeight: lineHeight.overline,
    letterSpacing: "0.08333em",
    textTransform: "uppercase",
  },
} as const;

export type TTypography = typeof typography;

/**
 * Text role scale — purpose-based type styles for the `src/ui/Text` system.
 * Values extracted from legacy component styles (EmptyStateScreen, InputLabel,
 * DashedLabelFrame, InputPlaceholder, ErrorMessage).
 */
export const typographyTokens = {
  display: {
    fontSize: "1.25rem",
    fontWeight: fontWeight.bold,
    lineHeight: 1.25,
  },
  title: {
    fontSize: "1rem",
    fontWeight: fontWeight.bold,
    lineHeight: 1.25,
    color: palette.extended.midnightBlue,
  },
  heading: {
    fontSize: "1rem",
    fontWeight: fontWeight.semibold,
    lineHeight: 1.25,
  },
  subheading: {
    fontSize: "1rem",
    fontWeight: fontWeight.medium,
    lineHeight: 1.25,
  },
  body: {
    fontSize: "1rem",
    fontWeight: fontWeight.regular,
    lineHeight: 1.5,
  },
  bodySmall: {
    fontSize: "0.875rem",
    fontWeight: fontWeight.regular,
    lineHeight: 1.43,
  },
  label: {
    fontSize: "1rem",
    fontWeight: fontWeight.medium,
    lineHeight: "1.25rem",
  },
  caption: {
    fontSize: "0.75rem",
    fontWeight: fontWeight.regular,
    lineHeight: 1.33,
  },
  overline: {
    fontSize: "0.6875rem",
    fontWeight: fontWeight.semibold,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  placeholder: {
    fontSize: "1rem",
    lineHeight: "1.25rem",
    color: palette.text.secondary,
  },
  helper: {
    fontSize: "0.875rem",
    fontWeight: fontWeight.regular,
  },
  error: {
    fontSize: "0.875rem",
    fontWeight: fontWeight.regular,
  },
} as const;

export type TTypographyToken = keyof typeof typographyTokens;
